import { useState, useEffect, useRef } from 'react';
import type { BulkUpload } from 'types';
import { bookService } from 'services/bookService';
import { useBulkUpload } from 'contexts/BulkUploadContext';
import { compressImage } from 'utils/imageHelper';
import './BookFormModal.css';

interface BulkUploadModalProps {
  onClose: () => void;
  onSubmitted: (uploads: BulkUpload[]) => void;
}

interface UploadPreview {
  file: File;
  preview: string;
  status: 'pending' | 'uploading' | 'queued' | 'processing' | 'completed' | 'skipped' | 'ERROR';
  message?: string;
  taskId?: string;
  bookTitle?: string;
  bookAuthor?: string;
}

export const BulkUploadModal = ({ onClose, onSubmitted }: BulkUploadModalProps) => {
  const { startUpload, updateItem } = useBulkUpload();
  const [uploadPreviews, setUploadPreviews] = useState<UploadPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const taskIdsRef = useRef<string[]>([]);
  const statusCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedProgressRef = useRef(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const compressedFiles = await Promise.all(
        Array.from(files).map(async (file) => {
          try {
            return await compressImage(file, 300);
          } catch (err) {
            console.error('Error compressing image:', err);
            return file;
          }
        })
      );

      const newPreviews: UploadPreview[] = compressedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        status: 'pending',
      }));

      setUploadPreviews(prev => [...prev, ...newPreviews]);

      if (!hasInitializedProgressRef.current) {
        const fileNames = compressedFiles.map(f => f.name);
        startUpload(fileNames);
        hasInitializedProgressRef.current = true;
      }

      await uploadForBackgroundProcessing(compressedFiles);
    }
  };

  const uploadForBackgroundProcessing = async (files: File[]) => {
    if (isUploading) return;
    setIsUploading(true);
    setError('');

    try {
      setUploadPreviews(prev => prev.map(p =>
        files.find(f => f === p.file) ? { ...p, status: 'uploading' } : p
      ));

      const result = await bookService.submitBackgroundBulkUpload(files);
      taskIdsRef.current = [...taskIdsRef.current, ...result.task_ids];

      setUploadPreviews(prev => prev.map(p => {
        const matchingFile = files.find(f => f === p.file);
        if (matchingFile) {
          const taskIndex = files.indexOf(matchingFile);
          return {
            ...p,
            status: 'queued',
            message: 'Queued for processing',
            taskId: result.task_ids[taskIndex],
          };
        }
        return p;
      }));

      // Update context with queued status
      files.forEach((file, index) => {
        updateItem(file.name, {
          status: 'queued',
          message: 'Queued for background processing',
        });
      });

      startStatusPolling(result.task_ids);

    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err.message || 'Error uploading files';
      setError(errorMessage);

      setUploadPreviews(prev => prev.map(p =>
        files.find(f => f === p.file)
          ? { ...p, status: 'ERROR', message: errorMessage }
          : p
      ));

      files.forEach(file => {
        updateItem(file.name, { status: 'ERROR', message: errorMessage });
      });
    } finally {
      setIsUploading(false);
    }
  };

  const startStatusPolling = (ids: string[]) => {
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
    }

    statusCheckIntervalRef.current = setInterval(async () => {
      try {
        const status = await bookService.getBackgroundUploadStatus(ids);

        if ('tasks' in status) {
          status.tasks.forEach(task => {
            setUploadPreviews(prev => prev.map(p => {
              if (p.taskId === task.id) {
                let newStatus: UploadPreview['status'] = 'queued';
                if (task.status === 'completed') {
                  // Check if it was skipped (message contains "Skipped")
                  if (task.message && task.message.includes('Skipped')) {
                    newStatus = 'skipped';
                  } else {
                    newStatus = 'completed';
                  }
                }
                else if (task.status === 'failed') newStatus = 'ERROR';
                else if (task.status === 'processing') newStatus = 'processing';

                return {
                  ...p,
                  status: newStatus,
                  message: task.message,
                  bookTitle: task.book_title,
                  bookAuthor: task.book_author,
                };
              }
              return p;
            }));

            const preview = uploadPreviews.find(p => p.taskId === task.id);
            if (preview) {
              const isSkipped = task.message && task.message.includes('Skipped');
              updateItem(preview.file.name, {
                status: isSkipped ? 'skipped' :
                        task.status === 'completed' ? 'completed' :
                        task.status === 'failed' ? 'ERROR' : 'processing',
                message: task.message,
                bookTitle: task.book_title,
                bookAuthor: task.book_author,
              });
            }
          });

          const allDone = status.tasks.every(t => t.status === 'completed' || t.status === 'failed');
          if (allDone) {
            stopStatusPolling();
          }
        }
      } catch (err) {
        console.error('Error checking status:', err);
      }
    }, 2000);
  };

  const stopStatusPolling = () => {
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
  };

  const removeFile = (file: File) => {
    setUploadPreviews(prev => {
      const toRemove = prev.find(p => p.file === file);
      if (toRemove?.preview) {
        URL.revokeObjectURL(toRemove.preview);
      }
      return prev.filter(p => p.file !== file);
    });
  };

  const handleClose = () => {
    stopStatusPolling();
    
    // Pass summary of completed uploads to parent
    const completedUploads: BulkUpload[] = uploadPreviews
      .filter(p => p.status === 'completed' || p.status === 'skipped' || p.status === 'ERROR')
      .map(p => ({
        id: Date.now() + Math.random(),
        title: p.bookTitle || 'Unknown',
        author_name: p.bookAuthor || 'Unknown',
        isbn: null,
        publisher: null,
        publication_year: null,
        category: null,
        description: null,
        cover_image_path: null,
        barcode_image_path: null,
        status: p.status === 'ERROR' ? 'ERROR' : p.status === 'skipped' ? 'REJECTED' : 'PENDING',
        admin_comment: null,
        error_message: p.status === 'ERROR' ? (p.message || null) : null,
        marked_as_done: false,
        uploaded_at: new Date().toISOString(),
        reviewed_at: null,
        reviewed_by: null,
      }));
    
    onSubmitted(completedUploads);
    
    uploadPreviews.forEach(p => {
      if (p.preview) {
        URL.revokeObjectURL(p.preview);
      }
    });
    onClose();
  };

  useEffect(() => {
    return () => {
      stopStatusPolling();
    };
  }, []);

  const uploadingCount = uploadPreviews.filter(p => p.status === 'uploading').length;
  const queuedCount = uploadPreviews.filter(p => p.status === 'queued').length;
  const processingCount = uploadPreviews.filter(p => p.status === 'processing').length;
  const completedCount = uploadPreviews.filter(p => p.status === 'completed').length;
  const skippedCount = uploadPreviews.filter(p => p.status === 'skipped').length;
  const errorCount = uploadPreviews.filter(p => p.status === 'ERROR').length;
  const pendingCount = uploadPreviews.filter(p => p.status === 'pending').length;
  const hasActiveTasks = uploadingCount > 0 || pendingCount > 0 || processingCount > 0;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content bulk-upload-modal-simple" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">
              {hasActiveTasks ? 'Uploading Files...' : 'Bulk Upload Books'}
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {completedCount > 0 && (
                <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>
                  ✓ {completedCount} completed
                </span>
              )}
              {skippedCount > 0 && (
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {skippedCount} skipped (duplicate)
                </span>
              )}
              {errorCount > 0 && (
                <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                  {errorCount} errors
                </span>
              )}
              <button className="modal-close" onClick={handleClose}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          <div className="bulk-upload-area">
            <label className="upload-dropzone">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                disabled={isUploading}
                className="file-input"
              />
              <div className="upload-icon">
                <span className="material-symbols-outlined">cloud_upload</span>
              </div>
              <h3>Drop barcode images here or click to browse</h3>
              <p>Supports multiple images (JPG, PNG)</p>
            </label>
          </div>

          {uploadPreviews.length > 0 && (
            <div className="upload-summary">
              <div className="summary-stats">
                {completedCount > 0 && (
                  <span className="stat completed">
                    <span className="material-symbols-outlined">check_circle</span>
                    {completedCount} Completed
                  </span>
                )}
                {skippedCount > 0 && (
                  <span className="stat skipped">
                    <span className="material-symbols-outlined">filter_alt</span>
                    {skippedCount} Skipped
                  </span>
                )}
                {processingCount > 0 && (
                  <span className="stat processing">
                    <span className="material-symbols-outlined spinning">progress_activity</span>
                    {processingCount} Processing
                  </span>
                )}
                {queuedCount > 0 && (
                  <span className="stat queued">
                    <span className="material-symbols-outlined">schedule</span>
                    {queuedCount} Queued
                  </span>
                )}
                {uploadingCount > 0 && (
                  <span className="stat uploading">
                    <span className="material-symbols-outlined spinning">progress_activity</span>
                    {uploadingCount} Uploading
                  </span>
                )}
                {errorCount > 0 && (
                  <span className="stat error">
                    <span className="material-symbols-outlined">error</span>
                    {errorCount} Errors
                  </span>
                )}
                <span className="stat total">Total: {uploadPreviews.length}</span>
              </div>
            </div>
          )}

          {uploadPreviews.length > 0 && (
            <div className="previews-grid">
              {uploadPreviews.map((preview, index) => (
                <div key={index} className={`preview-card status-${preview.status}`}>
                  <div className="preview-header">
                    <span className="preview-number">#{index + 1}</span>
                    <button
                      className="remove-btn"
                      onClick={() => removeFile(preview.file)}
                      disabled={preview.status === 'uploading' || preview.status === 'queued' || preview.status === 'processing'}
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>

                  <div className="preview-image">
                    <img src={preview.preview} alt="Barcode" />
                    {preview.status === 'uploading' && (
                      <div className="processing-overlay">
                        <span className="material-symbols-outlined spinning">progress_activity</span>
                        <span>Uploading...</span>
                      </div>
                    )}
                    {preview.status === 'processing' && (
                      <div className="processing-overlay">
                        <span className="material-symbols-outlined spinning">progress_activity</span>
                        <span>Processing...</span>
                      </div>
                    )}
                    {preview.status === 'completed' && (
                      <div className="success-overlay">
                        <span className="material-symbols-outlined">check_circle</span>
                        <span>Completed!</span>
                      </div>
                    )}
                    {preview.status === 'skipped' && (
                      <div className="skipped-overlay">
                        <span className="material-symbols-outlined">filter_alt</span>
                        <span>Skipped</span>
                      </div>
                    )}
                  </div>

                  <div className="preview-info">
                    {preview.status === 'skipped' ? (
                      <>
                        <p className="skipped-text">{preview.message || 'Duplicate ISBN'}</p>
                        <span className="status-badge skipped">Skipped</span>
                      </>
                    ) : preview.status === 'completed' ? (
                      <>
                        <p className="success-text">{preview.message || 'Completed'}</p>
                        {preview.bookTitle && <p className="book-title">{preview.bookTitle}</p>}
                        {preview.bookAuthor && <p className="book-author">by {preview.bookAuthor}</p>}
                        <span className="status-badge completed">Completed</span>
                      </>
                    ) : preview.status === 'processing' ? (
                      <>
                        <p className="processing-text">{preview.message || 'Processing...'}</p>
                        <span className="status-badge processing">Processing</span>
                      </>
                    ) : preview.status === 'uploading' ? (
                      <>
                        <p className="processing-text">Uploading to server...</p>
                        <span className="status-badge uploading">Uploading</span>
                      </>
                    ) : preview.status === 'queued' ? (
                      <>
                        <p className="success-text">Queued for processing</p>
                        <span className="status-badge queued">Queued</span>
                      </>
                    ) : preview.status === 'ERROR' ? (
                      <>
                        <p className="error-message">{preview.message}</p>
                        <span className="status-badge error">Error</span>
                      </>
                    ) : (
                      <>
                        <p className="pending-text">Waiting to upload...</p>
                        <span className="status-badge pending">Pending</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              {hasActiveTasks || completedCount > 0 ? 'Close & Continue in Background' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
